/* eslint complexity: ["error", {max: 20}] -- this applies for the entire file */
/* eslint max-lines-per-function: ["error", {max: 161}]  -- Due to long file */
/* eslint max-lines: ["error", {max: 280}]  -- Due to long file */
import { useState } from "react";

import isNil from "lodash/isNil";
import { Input } from "tamagui";

import { TextTestID } from "@/constants/TestID";
import { TextInputBaseStyles } from "@/constants/UI";
import Icon, { FAIconName } from "@/ui/core/Icon";
import { Box } from "@/ui/layout";
import { BorderlessInputShadowStyles } from "@/ui/theme/styles";
import type { InputProps as BaseInputProps } from "@/ui/theme/tamagui.config";
import { ColorToken, ColorTokenValues, ZIndexToken } from "@/ui/theme/tokens";
import { isAndroid, isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

const ToggleSecureTextEntryLabel = "Toggle visibility of ";
interface TextFieldProps extends BaseViewProps {
  autoCapitalize?: BaseInputProps["autoCapitalize"];
  autoComplete?: BaseInputProps["autoComplete"];
  hasClearButton?: boolean;
  hasDynamicHeight?: boolean;
  hasError?: boolean;
  icon?: FAIconName;
  isAutoCorrect?: BaseInputProps["autoCorrect"];
  isAutofocus?: boolean;
  isBorderless?: boolean;
  isDisabled?: boolean;
  isEditable?: boolean;
  isMultiline?: boolean;
  isSecureEntry?: boolean;
  keyboardType?: BaseInputProps["keyboardType"];
  onBlur?: BaseInputProps["onBlur"];
  onChange: (value: string) => void;
  onKeyPress?: BaseInputProps["onKeyPress"];
  onPressIn?: BaseInputProps["onPressIn"];
  onSelectionChange?: (e: {
    nativeEvent: { selection: { end: number; start: number } };
  }) => void;
  placeholder: string;
  value?: null | string;
}

type PaddingInlineStart = BaseInputProps["paddingInlineStart"];

const getInputFieldBaseStyles = ({
  isBorderless,
}: {
  isBorderless?: boolean;
}) => {
  if (isBorderless) {
    return {
      ...TextInputBaseStyles,
      ...BorderlessInputShadowStyles,
    };
  }

  return TextInputBaseStyles;
};

const getHeightStyles = ({
  hasDynamicHeight,
}: {
  hasDynamicHeight?: boolean;
}) => {
  if (hasDynamicHeight) {
    return {
      height: "auto",
      maxHeight: TextInputBaseStyles.height * 2.5,
      minHeight: TextInputBaseStyles.height,
    };
  }

  return {
    height: TextInputBaseStyles.height,
  };
};

const getIconColor = ({
  isDisabled,
  isFocused,
}: {
  isDisabled?: boolean;
  isFocused?: boolean;
}) => {
  if (isDisabled) {
    return ColorToken.NeutralSecondary;
  }

  if (isFocused) {
    return ColorToken.Primary100;
  }

  return ColorToken.NeutralPrimary;
};

const getBorderColor = ({
  hasError,
  isBorderless,
}: {
  hasError?: boolean;
  isBorderless?: boolean;
}) => {
  if (isBorderless) {
    return TextInputBaseStyles.backgroundColor;
  }

  return hasError
    ? ColorToken.NotificationCritical
    : TextInputBaseStyles.borderColor;
};

// eslint-disable-next-line no-restricted-syntax -- this core component has many props as it is a pass-through to the very complex Input tamagui component
const TextField = ({
  accessibilityLabel,
  autoCapitalize,
  autoComplete,
  hasClearButton = false,
  hasDynamicHeight,
  hasError = false,
  icon,
  isAutoCorrect,
  isAutofocus,
  isBorderless,
  isDisabled,
  isEditable = true,
  isMultiline,
  isSecureEntry,
  keyboardType,
  onBlur,
  onChange,
  onKeyPress,
  onPressIn,
  onSelectionChange,
  placeholder,
  testID,
  value: initialValue,
}: TextFieldProps) => {
  const value = isNil(initialValue) ? "" : initialValue;

  const [isSecureEntryEnabled, setIsSecureEntryEnabled] = useState<boolean>(
    isSecureEntry || false,
  );

  const handleOnChange = (newValue: string) => {
    if (!isDisabled) {
      onChange(newValue);
    }
  };

  const [isFocused, setIsFocused] = useState<boolean>(false);

  const iconColor = getIconColor({
    isDisabled,
    isFocused,
  });

  const paddingInlineStart: PaddingInlineStart = icon ? 38 : 12;
  const inputIconPadding = isAndroid() ? paddingInlineStart : undefined;
  const paddingInlineEnd: PaddingInlineStart = isSecureEntry ? 42 : 12;
  const baseTestID = joinTestIDs(testID, TextTestID.TextField);
  const borderColor = getBorderColor({ hasError, isBorderless });
  const shouldShowClearButton =
    !isSecureEntry && hasClearButton && value.length > 0;

  const baseStyles = getInputFieldBaseStyles({
    isBorderless,
  });

  const heightStyles = getHeightStyles({
    hasDynamicHeight,
  });

  return (
    <Box
      flexGrow={1}
      flexShrink={1}
      justifyContent="center"
      position="relative"
    >
      <Input
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...baseStyles}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...heightStyles}
        accessibilityLabel={accessibilityLabel}
        autoCapitalize={autoCapitalize}
        autoComplete={autoComplete}
        autoCorrect={isAutoCorrect}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={isAutofocus}
        backgroundColor={
          isDisabled
            ? ColorTokenValues[ColorToken.NeutralBackgroundSecondary]
            : ColorTokenValues[ColorToken.NeutralBackgroundPrimary]
        }
        borderColor={borderColor}
        disabled={isDisabled}
        // editable differs from disabled in that disabled ignores press events entirely
        // editable={false} still invokes onPressIn / onPressOut / other event handlers (useful for triggering a MultiSelectModal)
        // in terms of styling here, disabled also sets a background color, while editable={false} does not
        // Platform-specific: native uses 'editable', web uses 'readOnly'
        // eslint-disable-next-line react/jsx-props-no-spreading, @typescript-eslint/naming-convention
        {...(isWeb() ? { readOnly: !isEditable } : { editable: isEditable })}
        focusStyle={{
          borderColor: hasError
            ? ColorToken.NotificationCritical
            : ColorToken.Primary100,
          borderWidth: 1,
        }}
        // web-only: override Tamagui's default gray hover border with the current border color (no-op on native)
        hoverStyle={{ borderColor }}
        keyboardType={keyboardType}
        multiline={isMultiline}
        onBlur={(e) => {
          setIsFocused(false);
          onBlur?.(e);
        }}
        onChangeText={handleOnChange}
        onFocus={() => setIsFocused(true)}
        onKeyPress={onKeyPress}
        onPressIn={onPressIn}
        onSelectionChange={onSelectionChange}
        paddingInlineEnd={paddingInlineEnd}
        paddingInlineStart={paddingInlineStart}
        paddingLeft={inputIconPadding}
        placeholder={placeholder}
        placeholderTextColor={ColorToken.NeutralTertiary}
        testID={baseTestID}
        type={isSecureEntryEnabled ? "password" : undefined}
        value={value}
      />
      {isSecureEntry && (
        <Box
          onPress={() => setIsSecureEntryEnabled(!isSecureEntryEnabled)}
          position="absolute"
          right={16}
          zIndex={ZIndexToken.FloatingContent}
        >
          <Icon
            accessibilityLabel={`${ToggleSecureTextEntryLabel}${accessibilityLabel}`}
            color={iconColor}
            name={isSecureEntryEnabled ? FAIconName.EyeSlash : FAIconName.Eye}
            size="md"
            testID={joinTestIDs(baseTestID, "toggle-secure-entry")}
          />
        </Box>
      )}

      {icon && (
        <Box left={12} position="absolute">
          <Icon color={iconColor} name={icon} size="md" testID={baseTestID} />
        </Box>
      )}
      {shouldShowClearButton && (
        <Box
          onPress={() => handleOnChange("")}
          position="absolute"
          right={16}
          testID={joinTestIDs(baseTestID, "clear-button")}
          zIndex={ZIndexToken.FloatingContent}
        >
          <Icon
            accessibilityLabel={`Clear ${accessibilityLabel || "input"}`}
            color={iconColor}
            name={FAIconName.Times}
            size="md"
            testID={joinTestIDs(baseTestID, "clear-button-icon")}
          />
        </Box>
      )}
    </Box>
  );
};

export { getBorderColor, getInputFieldBaseStyles, ToggleSecureTextEntryLabel };
export type { TextFieldProps };
export default TextField;
